// src/pages/PermanentInvitationsPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  PlusIcon, 
  PencilSquareIcon, 
  TrashIcon, 
  ArrowLeftIcon, 
  MagnifyingGlassIcon,
  StarIcon,
  TagIcon,
  MusicalNoteIcon,
  UserGroupIcon,
  TicketIcon
} from "@heroicons/react/24/solid";
import { toast } from "react-toastify";
import AddPermanentInvitationModal from "../components/AddPermanentInvitationModal";
import EditPermanentInvitationModal from "../components/EditPermanentInvitationModal";
import ConfirmModal from "../components/ConfirmModal";
import Spinner from "../components/Spinner";
import { SERVER_BASE_URL } from "../config"; // Импорт централизованного URL

function PermanentInvitationsPage() {
  const { listId } = useParams();
  const navigate = useNavigate();

  const [permanentInvitations, setPermanentInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [invitationToEdit, setInvitationToEdit] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [invitationToDelete, setInvitationToDelete] = useState(null);
  const [listName, setListName] = useState(`Invitation List ${listId}`);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("invitee_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedInvitationIds, setSelectedInvitationIds] = useState([]);
  const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);

  // Функция для отображения иконки категории гостя
  const renderCategoryIcon = (category) => {
    switch (category) {
      case "VIP":
        return <StarIcon className="h-4 w-4 text-yellow-500" title="VIP" />;
      case "BACKSTAGE":
        return <TagIcon className="h-4 w-4 text-purple-500" title="Backstage" />;
      case "ARTIST":
        return <MusicalNoteIcon className="h-4 w-4 text-purple-500" title="Artist" />;
      case "STAFF":
        return <UserGroupIcon className="h-4 w-4 text-purple-500" title="Staff" />;
      case "REGULAR":
      default:
        return <TicketIcon className="h-4 w-4 text-gray-400" title="Regular" />;
    }
  };

  // Функция для определения, мобильное ли устройство
  const isMobile = () => {
    return window.innerWidth <= 768;
  };

  const fetchPermanentInvitations = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${SERVER_BASE_URL}/api/permanent-invitations/lists/${listId}/invitations/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to load permanent invitations");
      }
      const data = await response.json();
      setPermanentInvitations(data);
    } catch (error) {
      console.error("Error loading permanent invitations:", error);
      toast.error("Failed to load permanent invitations.");
    } finally {
      setLoading(false);
    }
  }, [listId]);

  const fetchListDetails = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${SERVER_BASE_URL}/api/permanent-invitations/lists/${listId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.name) {
          setListName(data.name);
        }
      }
    } catch (error) {
      console.error("Error loading list details:", error);
    }
  }, [listId]);

  useEffect(() => {
    fetchPermanentInvitations();
    fetchListDetails();
  }, [fetchPermanentInvitations, fetchListDetails]);

  const openAddModal = () => setShowAddModal(true);
  const closeAddModal = () => setShowAddModal(false);
  const openEditModal = (invitation) => {
    setInvitationToEdit(invitation);
    setShowEditModal(true);
  };
  const closeEditModal = () => {
    setInvitationToEdit(null);
    setShowEditModal(false);
  };
  const openConfirmModal = (invitation) => {
    setInvitationToDelete(invitation);
    setShowConfirmModal(true);
  };
  const closeConfirmModal = () => {
    setInvitationToDelete(null);
    setShowConfirmModal(false);
  };

  const handleDeleteInvitation = async (invitationId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${SERVER_BASE_URL}/api/permanent-invitations/invitations/${invitationId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to delete permanent invitation");
      }
      toast.success("Permanent invitation successfully deleted.");
      fetchPermanentInvitations();
    } catch (error) {
      console.error("Error deleting permanent invitation:", error);
      toast.error("Failed to delete permanent invitation.");
    }
  };

  const confirmDeleteInvitation = () => {
    if (invitationToDelete) {
      handleDeleteInvitation(invitationToDelete.id);
      closeConfirmModal();
    }
  };

  const handleSort = (criteria) => {
    if (criteria === sortBy) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(criteria);
      setSortOrder("asc");
    }
  };

  const renderSortIcon = (column) => {
    if (column !== sortBy) {
      return <span className="text-gray-400">–</span>;
    }
    return sortOrder === "asc" ? (
      <span className="text-gray-600" style={{ fontSize: "11px" }}>▲</span>
    ) : (
      <span className="text-gray-600" style={{ fontSize: "11px" }}>▼</span>
    );
  };

  const sortedInvitations = [...permanentInvitations].sort((a, b) => {
    const fieldA = a[sortBy]?.toString().toLowerCase() || "";
    const fieldB = b[sortBy]?.toString().toLowerCase() || "";
    return sortOrder === "asc"
      ? fieldA.localeCompare(fieldB)
      : fieldB.localeCompare(fieldA);
  });

  const filteredInvitations = sortedInvitations.filter((inv) => {
    const query = searchQuery.toLowerCase();
    return (
      (inv.invitee_name && inv.invitee_name.toLowerCase().includes(query)) ||
      (inv.invitee_email && inv.invitee_email.toLowerCase().includes(query))
    );
  });

  const handleCheckboxChangeInvitation = (id) => {
    setSelectedInvitationIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAllInvitations = () => {
    if (selectedInvitationIds.length === filteredInvitations.length) {
      setSelectedInvitationIds([]);
    } else {
      setSelectedInvitationIds(filteredInvitations.map((inv) => inv.id));
    }
  };

  const handleDeleteSelectedInvitations = async () => {
    try {
      await Promise.all(
        selectedInvitationIds.map((id) =>
          fetch(`${SERVER_BASE_URL}/api/permanent-invitations/invitations/${id}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          })
        )
      );
      fetchPermanentInvitations();
      setSelectedInvitationIds([]);
      setShowBulkConfirmModal(false);
      toast.success("Selected permanent invitations successfully deleted.");
    } catch (error) {
      console.error("Error deleting selected permanent invitations:", error);
      toast.error("Failed to delete selected permanent invitations.");
    }
  };

  const confirmBulkDeleteInvitations = async () => {
    await handleDeleteSelectedInvitations();
  };

  const handleInvitationCreated = () => {
    fetchPermanentInvitations();
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container mx-auto max-w-5xl px-4 py-12 mt-8" style={{ fontSize: isMobile() ? '0.9rem' : '1rem' }}>
        {/* Карточка верхнего блока */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate("/people", { state: { activeTab: "invitations" } })}
              className="flex items-center mr-4 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-1" />
              <span>Back</span>
            </button>
            <h1 className="text-2xl font-bold text-gray-800 truncate max-w-[70%]">{listName}</h1>
          </div>
          
          {/* Информационные карточки - Desktop view */}
          <div className="hidden sm:grid sm:grid-cols-3 gap-4 mb-6">
            <div className="p-4 rounded-lg  bg-indigo-50">
              <p className="text-sm text-gray-600 mb-1">Total Invitations</p>
              <p className="text-2xl font-bold text-gray-800">{permanentInvitations.length}</p>
            </div>
            
            <div className="p-4 rounded-lg b bg-indigo-50">
              <p className="text-sm text-gray-600 mb-1">Variants</p>
              <p className="text-2xl font-bold text-gray-800">
                {new Set(permanentInvitations.flatMap(inv => inv.variants?.map(v => v.type) || [])).size}
              </p>
            </div>
            
            <div className="p-4 rounded-lg  bg-indigo-50">
              <p className="text-sm text-gray-600 mb-1">Selected</p>
              <p className="text-2xl font-bold text-gray-800">
                {selectedInvitationIds.length}
              </p>
            </div>
          </div>
          
          {/* Mobile view - small square cards */}
          <div className="flex justify-evenly mb-4 gap-0 sm:hidden">
            <div className="py-2 rounded-lg  bg-indigo-50 w-[30%] flex flex-col justify-center px-1">
              <p className="text-[10px] text-gray-600 m-0 text-center">Invitations</p>
              <p className="text-lg font-bold text-gray-800 text-center m-0">
                {permanentInvitations.length}
              </p>
            </div>

            <div className="py-2 rounded-lg  bg-indigo-50 w-[30%] flex flex-col justify-center px-1">
              <p className="text-[10px] text-gray-600 m-0 text-center">Variants</p>
              <p className="text-lg font-bold text-gray-800 text-center m-0">
                {new Set(permanentInvitations.flatMap(inv => inv.variants?.map(v => v.type) || [])).size}
              </p>
            </div>

            <div className="py-2 rounded-lg  bg-indigo-50 w-[30%] flex flex-col justify-center px-1">
              <p className="text-[10px] text-gray-600 m-0 text-center">Selected</p>
              <p className="text-lg font-bold text-gray-800 text-center m-0">
                {selectedInvitationIds.length}
              </p>
            </div>
          </div>
          
          {/* Действия */}
          <div className="flex flex-col md:flex-row justify-between space-y-4 md:space-y-0">
            <div className="flex space-x-2">
              <button
                onClick={openAddModal}
                className="bg-indigo-600 text-white font-bold px-4 py-2 rounded flex items-center hover:bg-indigo-700 transition duration-200"
              >
                <PlusIcon className="mr-2 h-5 w-5" />
                Add Invitation
              </button>
              
              {selectedInvitationIds.length > 0 && (
                <button
                  onClick={() => setShowBulkConfirmModal(true)}
                  className="flex items-center bg-white text-gray-600 px-4 py-2 rounded hover:bg-gray-50 border border-gray-300 transition duration-200"
                >
                  <TrashIcon className="h-5 w-5 mr-2" />
                   ({selectedInvitationIds.length})
                </button>
              )}
            </div>
            
            {/* Поиск */}
            <div className="relative max-w-md w-full">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search invitations..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Таблица приглашений в отдельной карточке */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          {loading ? (
            <div className="py-20 flex justify-center items-center">
              <Spinner />
            </div>
          ) : filteredInvitations.length === 0 ? (
            <div className="py-10 text-center text-gray-500">
              {searchQuery ? "No invitations match your search." : "No permanent invitations created yet."}
            </div>
          ) : (
            <>
              {/* Desktop View - Table */}
              <div className="hidden sm:block overflow-x-auto">
                {/* Header Row с bulk selection */}
                <div className="grid grid-cols-[40px_1fr_1fr_1fr_80px] items-center bg-gray-100 rounded-t-lg px-4 py-2">
                  <div className="text-center">
                    <input
                      type="checkbox"
                      style={{ marginLeft: "5px" }}
                      checked={selectedInvitationIds.length === filteredInvitations.length && filteredInvitations.length > 0}
                      onChange={handleSelectAllInvitations}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                  </div>
                  <div
                    className="flex items-center cursor-pointer font-semibold text-gray-600"
                    onClick={() => handleSort("invitee_name")}
                  >
                    <span>Name</span>
                    <span className="ml-1">{renderSortIcon("invitee_name")}</span>
                  </div>
                  <div
                    className="flex items-center cursor-pointer font-semibold text-gray-600"
                    onClick={() => handleSort("invitee_email")}
                  >
                    <span>Email</span>
                    <span className="ml-1">{renderSortIcon("invitee_email")}</span>
                  </div>
                  <div className="font-semibold text-gray-600 text-center">Variants</div>
                  <div className="font-semibold text-gray-600 text-center">Actions</div>
                </div>
                
                <div>
                  {filteredInvitations.map((invitation, index) => (
                    <div
                      key={invitation.id}
                      className={`grid grid-cols-[40px_1fr_1fr_1fr_80px] items-center border-b border-indigo-100 p-2 hover:bg-indigo-50 transition duration-200 cursor-default ${
                        index % 2 === 0 ? "" : "bg-gray-50"
                      }`}
                    >
                      <div className="text-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          checked={selectedInvitationIds.includes(invitation.id)}
                          onChange={() => handleCheckboxChangeInvitation(invitation.id)}
                        />
                      </div>
                      <div className="text-base font-medium text-black px-2 overflow-hidden whitespace-nowrap text-ellipsis flex items-center gap-1">
                        {invitation.guest_category && renderCategoryIcon(invitation.guest_category)}
                        {!invitation.guest_category && invitation.variants && invitation.variants.length > 0 && invitation.variants[0].category && 
                          renderCategoryIcon(invitation.variants[0].category)}
                        {invitation.invitee_name}
                      </div>
                      <div className="text-sm font-medium text-gray-600 px-2 overflow-hidden whitespace-nowrap text-ellipsis">
                        {invitation.invitee_email}
                      </div>
                      <div className="flex flex-wrap gap-1 justify-center">
                        {invitation.variants &&
                          invitation.variants
                            .filter((variant) => variant.limit > 0)
                            .map((variant, idx) => (
                              <span
                                key={idx}
                                className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full text-xs border border-indigo-300 flex items-center"
                              >
                                <span className="mr-1">
                                  {renderCategoryIcon(variant.category || "REGULAR")}
                                </span>
                                {variant.type} x {variant.limit}
                              </span>
                            ))}
                      </div>
                      <div className="flex space-x-2 justify-center px-2">
                        <button
                          onClick={() => openEditModal(invitation)}
                          className="text-gray-500 hover:text-yellow-700"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openConfirmModal(invitation)}
                          className="text-gray-500 hover:text-red-600"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mobile View - Cards */}
              <div className="sm:hidden block">
                {/* Mobile header with select all */}
                <div className="flex justify-between items-center py-1.5 px-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                  <div className="text-sm font-medium">Invitations</div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="select-all-mobile"
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      checked={selectedInvitationIds.length === filteredInvitations.length && filteredInvitations.length > 0}
                      onChange={handleSelectAllInvitations}
                    />
                    <label htmlFor="select-all-mobile" className="ml-1 text-xs text-gray-600">All</label>
                  </div>
                </div>
                
                <div className="divide-y divide-gray-200">
                  {filteredInvitations.map((invitation) => (
                    <div key={invitation.id} className="bg-white p-3">
                      <div className="flex items-center mb-1">
                        <input
                          type="checkbox"
                          checked={selectedInvitationIds.includes(invitation.id)}
                          onChange={() => handleCheckboxChangeInvitation(invitation.id)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mr-2"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="font-medium truncate mr-2 flex items-center gap-1">
                          {invitation.guest_category && renderCategoryIcon(invitation.guest_category)}
                          {!invitation.guest_category && invitation.variants && invitation.variants.length > 0 && invitation.variants[0].category && 
                            renderCategoryIcon(invitation.variants[0].category)}
                          {invitation.invitee_name}
                        </div>
                        <div className="flex flex-wrap gap-1 ml-auto">
                          {invitation.variants &&
                            invitation.variants
                              .filter((variant) => variant.limit > 0)
                              .map((variant, idx) => (
                                <span
                                  key={idx}
                                  className="bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded-full text-xs border border-indigo-300 whitespace-nowrap flex items-center"
                                >
                                  <span className="mr-1">
                                    {renderCategoryIcon(variant.category || "REGULAR")}
                                  </span>
                                  {variant.type} x {variant.limit}
                                </span>
                              ))}
                        </div>
                      </div>
                      <div className="flex items-center">
                        <div className="text-gray-600 text-sm truncate flex-grow">{invitation.invitee_email}</div>
                        <div className="flex space-x-3 ml-2 flex-shrink-0">
                          <button
                            onClick={() => openEditModal(invitation)}
                            className="text-gray-500 hover:text-yellow-700"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openConfirmModal(invitation)}
                            className="text-gray-500 hover:text-red-600"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Пустое состояние, когда нет приглашений и нет поискового запроса */}
        {!loading && filteredInvitations.length === 0 && !searchQuery && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No invitations found</h3>
            <p className="text-gray-500 mb-6">Create your first invitation to get started.</p>
            <button
              onClick={openAddModal}
              className="bg-indigo-600 text-white font-bold px-4 py-2 rounded hover:bg-indigo-700 transition duration-200"
            >
              <PlusIcon className="inline-block h-5 w-5 mr-2" />
              Add Invitation
            </button>
          </div>
        )}

        {/* Модальные окна */}
        {showAddModal && (
          <AddPermanentInvitationModal
            listId={Number(listId)}
            isOpen={showAddModal}
            onClose={closeAddModal}
            onCreated={handleInvitationCreated}
          />
        )}
        {showEditModal && invitationToEdit && (
          <EditPermanentInvitationModal
            isOpen={showEditModal}
            invitation={invitationToEdit}
            onClose={closeEditModal}
            onUpdated={() => {
              fetchPermanentInvitations();
              closeEditModal();
            }}
          />
        )}
        <ConfirmModal
          isOpen={showConfirmModal}
          onClose={closeConfirmModal}
          onConfirm={confirmDeleteInvitation}
          message="Are you sure you want to delete this permanent invitation?"
        />
        <ConfirmModal
          isOpen={showBulkConfirmModal}
          onClose={() => setShowBulkConfirmModal(false)}
          onConfirm={confirmBulkDeleteInvitations}
          message="Are you sure you want to delete the selected permanent invitations?"
        />
      </div>
    </div>
  );
}

export default PermanentInvitationsPage;